/**
 * Responsibility: answer "is this tile ready yet" for the tile manager, building it on a worker thread.
 *
 * The whole asynchronous machinery lives behind one method that returns either a finished tile or `null`.
 * That shape is what kept the tile manager almost unchanged: it already re-examines the same candidates
 * every frame, so "not yet" costs nothing and needs no callbacks, no promises, and no queue of its own.
 *
 * ## Why it falls back to building in place
 *
 * A worker can fail to start — an unusual browser, a restrictive policy, a file:// page — and a world that
 * silently never loads terrain is a far worse failure than a world that stutters. So a failure here degrades
 * to exactly the behaviour that existed before the worker, which is the error-boundary rule: the caller gets
 * a valid result or a clearly worse one, never a broken one.
 *
 * Contains no Three.js and no DOM. `Worker` is a platform API rather than the document, and it is injected,
 * so this module runs in Node under test.
 */

import { TILES } from '../config.js';

/**
 * Create a tile source.
 *
 * @param {object} options
 * @param {(originX: number, originZ: number) => object} options.buildTile - Synchronous build on this
 *   thread. Used for priming, where the point is to finish rather than to stay smooth, and as the fallback
 *   if the worker cannot run.
 * @param {() => Worker} [options.createWorker] - Injected so tests can supply a fake and so a failure to
 *   construct one is handled here rather than crashing startup.
 * @param {(message: string, error?: unknown) => void} [options.warn] - Where a worker failure is reported.
 * @returns {object}
 */
export function createTileSource({
  buildTile,
  createWorker = () => new Worker(new URL('./tileWorker.js', import.meta.url), { type: 'module' }),
  warn = (message, error) => console.warn(message, error),
}) {
  if (typeof buildTile !== 'function') {
    throw new Error('createTileSource: buildTile must be a function');
  }

  /** Finished tiles by key, waiting to be collected. */
  const ready = new Map();

  /** Keys currently being built by the worker. */
  const pending = new Set();

  /**
   * Which world is being asked about, counted up on every seed change.
   *
   * Tiles in flight when the seed changes are terrain from somewhere else. Dropping them by generation is
   * cheaper and more obviously correct than trying to cancel work already started.
   */
  let generation = 0;

  let seed = null;
  let worker = null;
  let failed = false;

  /** Whether the world being asked about has a railway in it, which changes the ground the worker builds. */
  let railway = false;

  /**
   * How far along the route the host has surveyed.
   *
   * Sent with every build, because the worker cannot know it and the corridor cannot be shaped without it —
   * see the note in `tileWorker.js`. Zero in a world with no railway, where nothing reads it.
   */
  let preparedTo = 0;

  const keyFor = (originX, originZ) => `${originX},${originZ}`;

  function handleMessage(event) {
    const message = event.data;
    if (message.type !== 'tile' || message.generation !== generation) return;

    const key = keyFor(message.originX, message.originZ);
    pending.delete(key);
    ready.set(key, message.tile);
  }

  function handleFailure(error) {
    if (failed) return;
    failed = true;

    // Everything queued is now never coming; the synchronous path will pick those tiles up next frame.
    pending.clear();
    ready.clear();

    warn('[vibe-flyer] tile worker unavailable, building tiles on the main thread', error);
  }

  /** Start the worker, or decide once and for all that there will not be one. */
  function ensureWorker() {
    if (worker !== null || failed) return worker;

    try {
      worker = createWorker();
      worker.onmessage = handleMessage;

      /*
        Both, and neither is optional. `onerror` covers a worker that throws while running; `onmessageerror`
        covers a result that cannot be delivered, which for a message carrying half a megabyte of typed
        arrays is a real possibility rather than a formality.
      */
      worker.onerror = handleFailure;
      worker.onmessageerror = handleFailure;
    } catch (error) {
      handleFailure(error);
      return null;
    }

    if (seed !== null) postSeed();
    return worker;
  }

  function postSeed() {
    worker.postMessage({ type: 'seed', seed, generation, railway });
  }

  return {
    /**
     * Point the source at a world, discarding anything in flight for the previous one.
     *
     * @param {number} nextSeed - Already normalised by `createWorld`.
     */
    useSeed(nextSeed, options = {}) {
      seed = nextSeed;
      railway = options.railway === true;
      preparedTo = 0;
      generation += 1;

      ready.clear();
      pending.clear();

      const started = ensureWorker();
      if (started !== null) postSeed();
    },

    /**
     * Tell the source how far the route has been surveyed.
     *
     * Only meaningful with a railway. Held rather than passed per call so `requestTile` keeps the signature the
     * tile manager already calls it with — the manager knows nothing about railways and should not have to.
     *
     * @param {number} distance - Along the line, in world units.
     */
    setPreparedTo(distance) {
      if (Number.isFinite(distance)) preparedTo = distance;
    },

    /**
     * A finished tile, or `null` if it is not ready.
     *
     * Asking is what queues the work, so a caller that keeps asking each frame eventually gets an answer.
     * Never throws and never blocks: the slow path is the fallback build, which is what used to happen every
     * time.
     *
     * @param {number} originX
     * @param {number} originZ
     * @returns {object|null}
     */
    requestTile(originX, originZ) {
      const key = keyFor(originX, originZ);

      const finished = ready.get(key);
      if (finished !== undefined) {
        ready.delete(key);
        return finished;
      }

      if (failed) return buildTile(originX, originZ);

      const started = ensureWorker();
      if (started === null) return buildTile(originX, originZ);

      if (pending.has(key)) return null;

      /*
        A cap on work in flight, not on work queued. Without it, a fast turn asks for a whole new arc of tiles
        at once and the worker spends a second on tiles that have already gone out of range again — the same
        reason the manager builds nearest-first.
      */
      if (pending.size >= TILES.MAX_TILES_IN_FLIGHT) return null;

      pending.add(key);
      worker.postMessage({ type: 'build', generation, originX, originZ, preparedTo });

      return null;
    },

    /** How many tiles the worker is working on. For the readout, and for tests. */
    getPendingCount() {
      return pending.size;
    },

    /** Whether tiles are being built on this thread after all. Worth showing, since it changes the feel. */
    isFallback() {
      return failed;
    },

    /** Stop the worker. */
    dispose() {
      if (worker !== null) {
        worker.terminate();
        worker = null;
      }
      ready.clear();
      pending.clear();
    },
  };
}
